import AdventerousImg from 'src/assets/icons/template-previews/adventurous.png';
import BoldImg from 'src/assets/icons/template-previews/bold.png';
import DecoratedImg from 'src/assets/icons/template-previews/decorated.png';
import ElegantImg from 'src/assets/icons/template-previews/elegant.png';
import MinimalistImg from 'src/assets/icons/template-previews/minimalist.png';
import RobustImg from 'src/assets/icons/template-previews/robust.png';
import RusticImg from 'src/assets/icons/template-previews/rustic.png';
import StrikingImg from 'src/assets/icons/template-previews/striking.png';
import { doorStylesByPaletteName } from 'src/modules/designStudio/helpers/data/DoorPaletteStyles';
import type {
  Structure,
  WallStylePolicy,
} from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import {
  EdgeOrientation,
  EdgePosition,
  PartitionGuideline,
  PartitionSide,
  WallDirection,
} from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import type { VariationDescriptor } from 'src/modules/designStudio/helpers/data/TemplateFeaturedVariations';
import { FeaturedTemplateVariations } from 'src/modules/designStudio/helpers/data/TemplateFeaturedVariations';

import { LABEL_IDS } from '../../constants/productCatalog';

import type { TemplatePaletteConfig } from './PaletteStyles';
import { ACCENTED_PALETTES, SIMPLE_PALETTES } from './PaletteStyles';

export type TemplateWithIcon = {
  style: Structure;
  img: string;
  palettes: TemplatePaletteConfig[];
};

const nonMasonryWalls = [
  LABEL_IDS.HORIZONTAL_SIDING_ID,
  LABEL_IDS.OTHER_SIDING_ID,
  LABEL_IDS.SHINGLED_SIDING_ID,
  LABEL_IDS.VERTICAL_SIDING_ID,
  LABEL_IDS.VINYL_ID,
  LABEL_IDS.WOOD_ID,
  LABEL_IDS.WRAP_ID,
  LABEL_IDS.TUDOR_ID,
  LABEL_IDS.UNKNOWN_ID,
  LABEL_IDS.STUCCO_ID,
];

const TEXTURE_IDS: Record<number, VariationDescriptor> = {
  [LABEL_IDS.HORIZONTAL_SIDING_ID]: FeaturedTemplateVariations.I,
  [LABEL_IDS.VERTICAL_SIDING_ID]: FeaturedTemplateVariations.J,
  [LABEL_IDS.SHINGLED_SIDING_ID]: FeaturedTemplateVariations.A,
};

const templateTrialBasePolicy: WallStylePolicy = {
  onLabels: nonMasonryWalls,
};

const templateTrialDecoratedFront: WallStylePolicy = {
  onLabels: nonMasonryWalls,
  onSides: [WallDirection.FRONT],
};

const templateTrialGableTopPolicy: WallStylePolicy = {
  ...templateTrialDecoratedFront,
  hasPartitions: [
    {
      usesConstraints: [
        {
          cutAlong: PartitionGuideline.GABLE_CUT,
          keepSide: PartitionSide.ABOVE,
        },
      ],
    },
  ],
};

const orderedPalettes = (
  n: number,
  masterList: TemplatePaletteConfig[],
): TemplatePaletteConfig[] => {
  const palettes = [...masterList];
  palettes.splice(0, 0, ...palettes.splice(n, 1));
  return palettes;
};

const ALL_PALETTES = [...SIMPLE_PALETTES, ...ACCENTED_PALETTES];

const templateTrial1: TemplateWithIcon = {
  style: {
    designName: 'Minimalist',
    doorStyles: doorStylesByPaletteName.Minimalist.doorStyles,
    shutterStyles: [
      {
        policy: { onCurrentShutters: 'remove' },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#737e83',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {},
            appearance: {
              width: 0.1524,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
          {
            policy: {
              onOrientations: [EdgeOrientation.HORIZONTAL],
            },
            appearance: {
              width: 0.2032,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
      },
    ],
    roofStyles: [
      {
        policy: {},
        appearance: FeaturedTemplateVariations.C,
      },
    ],
  },
  img: MinimalistImg,
  palettes: orderedPalettes(3, SIMPLE_PALETTES),
};

const templateTrial2: TemplateWithIcon = {
  img: ElegantImg,
  style: {
    designName: 'Elegant',
    doorStyles: doorStylesByPaletteName.Elegant.doorStyles,
    roofStyles: [
      {
        policy: {},
        appearance: FeaturedTemplateVariations.G,
      },
    ],
    shutterStyles: [
      {
        policy: { onCurrentShutters: 'remove' },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#a1a797',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {},
            appearance: {
              width: 0.1016,
              texture: {
                color: '#c8c0b4',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
          {
            policy: {
              onOrientations: [EdgeOrientation.HORIZONTAL],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#c8c0b4',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
      },
      {
        name: 'accent',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.VERTICAL_SIDING_ID],
          color: '#7b7873',
        },
        trims: [
          {
            policy: {
              withinFaces: [EdgePosition.EXTERIOR_BOTTOM],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#c8c0b4',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
        policy: { ...templateTrialGableTopPolicy },
      },
    ],
  },
  palettes: orderedPalettes(10, ALL_PALETTES),
};

const templateTrial3: TemplateWithIcon = {
  img: RobustImg,
  style: {
    designName: 'Robust',
    doorStyles: doorStylesByPaletteName.Robust.doorStyles,
    roofStyles: [
      {
        policy: {},
        appearance: FeaturedTemplateVariations.E,
      },
    ],
    shutterStyles: [
      {
        policy: { onCurrentShutters: 'remove' },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#b2aeab',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {
              withinFaces: [
                EdgePosition.INTERIOR_TOP,
                EdgePosition.INTERIOR_BOTTOM,
                EdgePosition.INTERIOR_SIDE,
              ],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
          {
            policy: {
              withinFaces: [EdgePosition.EXTERIOR_SIDE],
            },
            appearance: {
              width: 0.2032,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
      },
      {
        name: 'accent',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.SHINGLED_SIDING_ID],
          color: '#b2aeab',
        },
        policy: templateTrialGableTopPolicy,
      },
    ],
  },
  palettes: orderedPalettes(4, ALL_PALETTES),
};

const templateTrial4: TemplateWithIcon = {
  img: DecoratedImg,
  style: {
    designName: 'Decorated',
    doorStyles: doorStylesByPaletteName.Decorated.doorStyles,
    roofStyles: [
      {
        policy: {},
        appearance: FeaturedTemplateVariations.F,
      },
    ],
    shutterStyles: [
      {
        policy: { onCurrentShutters: 'remove' },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#d8cfc3',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {
              onOrientations: [
                EdgeOrientation.HORIZONTAL,
                EdgeOrientation.VERTICAL,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
          {
            policy: {
              onOrientations: [EdgeOrientation.HORIZONTAL],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
      },
      {
        name: 'fronts',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.VERTICAL_SIDING_ID],
          color: '#d8cfc3',
        },
        trims: [
          {
            policy: {
              onOrientations: [
                EdgeOrientation.HORIZONTAL,
                EdgeOrientation.VERTICAL,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
          {
            policy: {
              withinFaces: [EdgePosition.EXTERIOR_SIDE],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
        policy: templateTrialDecoratedFront,
      },
    ],
  },
  palettes: orderedPalettes(8, ALL_PALETTES),
};

const templateTrial5: TemplateWithIcon = {
  img: RusticImg,
  style: {
    designName: 'Rustic',
    doorStyles: doorStylesByPaletteName.Rustic.doorStyles,
    roofStyles: [
      {
        policy: {},
        appearance: FeaturedTemplateVariations.H,
      },
    ],
    shutterStyles: [
      {
        policy: { onCurrentShutters: 'remove' },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#b5b9b5',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {},
            appearance: {
              width: 0.1016,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
          {
            policy: {
              onOrientations: [EdgeOrientation.VERTICAL],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
      },
      {
        name: 'fronts',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.SHINGLED_SIDING_ID],
          color: '#b5b9b5',
        },
        trims: [
          {
            policy: {
              withinFaces: [
                EdgePosition.INTERIOR_TOP,
                EdgePosition.INTERIOR_BOTTOM,
                EdgePosition.INTERIOR_SIDE,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
          {
            policy: {
              withinFaces: [EdgePosition.EXTERIOR_SIDE],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
        policy: templateTrialDecoratedFront,
      },
    ],
  },
  palettes: orderedPalettes(7, ALL_PALETTES),
};

const templateTrial6: TemplateWithIcon = {
  img: StrikingImg,
  style: {
    designName: 'Striking',
    doorStyles: doorStylesByPaletteName.Striking.doorStyles,
    roofStyles: [
      {
        policy: {},
        appearance: FeaturedTemplateVariations.C,
      },
    ],
    shutterStyles: [
      {
        policy: { onCurrentShutters: 'remove' },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.VERTICAL_SIDING_ID],
          color: '#dfe0dd',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {
              withinFaces: [
                EdgePosition.INTERIOR_TOP,
                EdgePosition.INTERIOR_BOTTOM,
                EdgePosition.INTERIOR_SIDE,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
          {
            policy: {
              onOrientations: [
                EdgeOrientation.HORIZONTAL,
                EdgeOrientation.SLOPED,
              ],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#dfe0dd',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
      },
    ],
  },
  palettes: orderedPalettes(0, SIMPLE_PALETTES),
};

const templateTrial7: TemplateWithIcon = {
  img: BoldImg,
  style: {
    designName: 'Bold',
    doorStyles: doorStylesByPaletteName.Bold.doorStyles,
    roofStyles: [
      {
        policy: {},
        appearance: FeaturedTemplateVariations.C,
      },
    ],
    shutterStyles: [
      {
        policy: { onCurrentShutters: 'remove' },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.SHINGLED_SIDING_ID],
          color: '#434447',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {
              withinFaces: [
                EdgePosition.INTERIOR_TOP,
                EdgePosition.INTERIOR_BOTTOM,
                EdgePosition.INTERIOR_SIDE,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#434447',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
      },
    ],
  },
  palettes: orderedPalettes(2, SIMPLE_PALETTES),
};

const templateTrial8: TemplateWithIcon = {
  img: AdventerousImg,
  style: {
    designName: 'Adventurous',
    doorStyles: doorStylesByPaletteName.Adventurous.doorStyles,
    roofStyles: [
      {
        policy: {},
        appearance: {
          ...FeaturedTemplateVariations.C,
        },
      },
    ],
    shutterStyles: [
      {
        policy: { onCurrentShutters: 'remove' },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#dfe0dd',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {},
            appearance: {
              width: 0.1016,
              texture: {
                color: '#434447',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
          {
            policy: {
              onOrientations: [EdgeOrientation.HORIZONTAL],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#434447',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
      },
      {
        name: 'fronts',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.VERTICAL_SIDING_ID],
          color: '#434447',
        },
        trims: [
          {
            policy: {},
            appearance: {
              width: 0.1016,
              texture: {
                color: '#434447',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
          {
            policy: {
              withinFaces: [EdgePosition.EXTERIOR_SIDE],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#434447',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
        policy: templateTrialDecoratedFront,
      },
      {
        name: 'tops',
        appearance: {
          ...TEXTURE_IDS[LABEL_IDS.VERTICAL_SIDING_ID],
          color: '#dfe0dd',
        },
        trims: [
          {
            policy: {
              withinFaces: [
                EdgePosition.EXTERIOR_TOP,
                EdgePosition.EXTERIOR_SIDE,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#434447',
                ...FeaturedTemplateVariations.B,
              },
            },
          },
        ],
        policy: templateTrialGableTopPolicy,
      },
    ],
  },
  palettes: orderedPalettes(1, ACCENTED_PALETTES),
};

const TEMPLATE_STYLES: TemplateWithIcon[] = [
  templateTrial1,
  templateTrial2,
  templateTrial3,
  templateTrial4,
  templateTrial5,
  templateTrial6,
  templateTrial7,
  templateTrial8,
];

export const TEMPLATE_STYLES_MANAGER = {
  getByIndex: (index: number): TemplateWithIcon =>
    JSON.parse(JSON.stringify(TEMPLATE_STYLES[index])) as TemplateWithIcon,

  getArray: (): TemplateWithIcon[] =>
    TEMPLATE_STYLES.map(s => JSON.parse(JSON.stringify(s)) as TemplateWithIcon),
};
